import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, FileArrowDown, Handshake, ShieldCheck, SealCheck, Bank } from "@phosphor-icons/react";

const buyer = [
  { icon: MagnifyingGlass, t: "Browse & filter", d: "Explore verified lots by category, location, unit count, and value." },
  { icon: FileArrowDown, t: "Download sanitized Excel", d: "Log in to access clean inventory files. Seller cost & identity data are always removed." },
  { icon: Handshake, t: "Express interest", d: "Submit an EOI with your indicative price and notes — visible only to SparesHub." },
  { icon: ShieldCheck, t: "Escrow closure", d: "We coordinate inspection, negotiation, and escrow. Funds release only on your sign-off." },
];

const seller = [
  { icon: FileArrowDown, t: "Submit your lot", d: "Upload inventory Excel, add media, and set your commercials in a guided flow." },
  { icon: SealCheck, t: "SparesHub verifies", d: "Our team reviews data quality, media, and pricing, then sanitizes the buyer-facing file." },
  { icon: MagnifyingGlass, t: "Go live", d: "Approved lots publish to verified buyers. Your identity stays private until you agree to a deal." },
  { icon: Bank, t: "Get paid via escrow", d: "We handle negotiation and escrow. You receive funds after the buyer signs off." },
];

function Track({ title, tone, items }) {
  return (
    <div className={`rounded-sm border border-border ${tone}`}>
      <div className="border-b border-border px-7 py-5">
        <h2 className="font-display text-xl font-bold text-navy">{title}</h2>
      </div>
      <div className="divide-y divide-border">
        {items.map((s, i) => (
          <div key={s.t} className="flex items-start gap-4 p-6">
            <span className="stat mt-1 text-sm font-bold text-brand">0{i + 1}</span>
            <s.icon className="mt-0.5 h-7 w-7 shrink-0 text-navy" />
            <div>
              <div className="font-display font-semibold text-navy">{s.t}</div>
              <div className="text-sm text-muted-foreground">{s.d}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HowItWorks() {
  return (
    <div>
      <section className="border-b border-border bg-navy py-16 text-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">How it works</span>
          <h1 className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-5xl">A managed marketplace, not a free-for-all.</h1>
          <p className="mt-4 max-w-2xl text-lg text-white/70">SparesHub sits between buyer and seller — verifying lots, sanitizing data, and holding funds in escrow so bulk deals close cleanly.</p>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-2">
          <Track title="For Buyers" tone="bg-white" items={buyer} />
          <Track title="For Sellers" tone="bg-navy-50" items={seller} />
        </div>
        <div className="mt-14 flex flex-col items-center gap-4 rounded-sm border border-border bg-navy p-10 text-center text-white">
          <ShieldCheck weight="fill" className="h-10 w-10 text-ok" />
          <h3 className="font-display text-2xl font-bold">Escrow protects both sides</h3>
          <p className="max-w-2xl text-white/70">Buyer funds are held by an RBI-regulated escrow partner and released to the seller only after the buyer inspects and accepts the lot. No party carries settlement risk.</p>
          <div className="mt-2 flex gap-3">
            <Button asChild className="rounded-sm bg-brand text-white hover:bg-brand-hover"><Link to="/register">Get started</Link></Button>
            <Button asChild variant="outline" className="rounded-sm border-white/25 bg-transparent text-white hover:bg-white hover:text-navy"><Link to="/lots">Browse lots</Link></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
